import React, { memo, useMemo } from 'react';
import { Handle, Position } from 'reactflow';
import { Box, Typography, Paper, IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import MessageSquare from '@mui/icons-material/Message';
import Zap from '@mui/icons-material/OfflineBolt';
import Split from '@mui/icons-material/CallSplit';
import CodeIcon from '@mui/icons-material/Code';
import AlertCircle from '@mui/icons-material/ErrorOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import DataObjectIcon from '@mui/icons-material/DataObject';
import PhoneForwardedIcon from '@mui/icons-material/PhoneForwarded';
import {
  getNodeExitPoints,
  getRouteStyle,
  getNodeSubtitle,
} from '../utils/flowUtils';
import { NODE_TYPES, HANDLE_COLOR } from '../constants';

export function NodeIcon({ type }) {
  const iconProps = { sx: { fontSize: 20 } };
  switch (type) {
    case NODE_TYPES.TRIGGER:
      return <Zap {...iconProps} />;
    case NODE_TYPES.SWITCH:
      return <Split sx={{ fontSize: 20, transform: 'rotate(90deg)' }} />;
    case NODE_TYPES.SEND_MESSAGE:
    case NODE_TYPES.SEND_MESSAGE_AND_WAIT:
      return <MessageSquare {...iconProps} />;
    case NODE_TYPES.MAKE_HTTP_REQUEST:
      return <CodeIcon {...iconProps} />;
    case NODE_TYPES.PLAY:
    case NODE_TYPES.GATHER_INPUT_ON_CALL:
      return <PlayCircleOutlineIcon {...iconProps} />;
    case NODE_TYPES.SET_VARIABLE:
      return <DataObjectIcon {...iconProps} />;
    case NODE_TYPES.DIAL:
      return <PhoneForwardedIcon {...iconProps} />;
    default:
      return <AlertCircle {...iconProps} />;
  }
}

/** Minimum width so exit handles don’t overlap; extra width per handle for connectors. */
const MIN_NODE_WIDTH = 420;
const BASE_NODE_WIDTH = 320;
const EXTRA_WIDTH_PER_EXIT = 70;

function CustomNode({ data, selected }) {
  const { nodeType, original, onDelete, readOnly } = data;
  const exits = useMemo(() => getNodeExitPoints(original), [original]);

  const nodeWidth = useMemo(() => {
    if (exits.length <= 2) return MIN_NODE_WIDTH;
    return Math.max(
      MIN_NODE_WIDTH,
      BASE_NODE_WIDTH + (exits.length - 2) * EXTRA_WIDTH_PER_EXIT,
    );
  }, [exits.length]);

  const subtitle = getNodeSubtitle(nodeType);

  return (
    <Paper
      elevation={selected ? 4 : 1}
      sx={{
        minWidth: nodeWidth,
        width: nodeWidth,
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        transition: 'all 0.2s',
        '&:hover': {
          boxShadow: selected ? 4 : 3,
        },
      }}>
      {nodeType !== 'TRIGGER' && (
        <Handle
          type="target"
          position={Position.Top}
          isConnectable={!readOnly}
          style={{
            width: 16,
            height: 16,
            backgroundColor: HANDLE_COLOR,
            top: -8,
          }}
        />
      )}

      <Box
        sx={{
          p: 1.5,
          pr: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 1,
          minHeight: 56,
        }}>
        <Box
          sx={{
            flexShrink: 0,
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.disabled',
          }}>
          <NodeIcon type={nodeType} />
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <Typography
            variant="h6"
            fontWeight="bold"
            color="text.primary"
            noWrap
            sx={{ width: '100%', textAlign: 'center' }}>
            {original.id}
          </Typography>
          <Typography
            variant="caption"
            fontWeight="medium"
            color="text.secondary"
            noWrap
            sx={{ width: '100%', textAlign: 'center' }}>
            {subtitle}
          </Typography>
        </Box>
        {onDelete && !readOnly && nodeType !== NODE_TYPES.TRIGGER && (
          <Box
            sx={{
              flexShrink: 0,
              width: 40,
              display: 'flex',
              justifyContent: 'center',
            }}>
            {selected ? (
              <Tooltip title="Delete widget">
                <IconButton
                  size="small"
                  color="error"
                  onClick={e => {
                    e.stopPropagation();
                    onDelete();
                  }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>
        )}
      </Box>

      <Box
        sx={{
          p: 2.5,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 60,
        }}>
        {original.attributes?.input && (
          <Box
            sx={{
              width: '100%',
              bgcolor: 'neutral.subtle',
              p: 1,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'neutral.muted',
              mb: 1,
            }}>
            <Typography
              variant="body2"
              fontWeight="medium"
              color="text.primary"
              align="center"
              sx={{ wordBreak: 'break-word' }}>
              {original.attributes.input}
            </Typography>
          </Box>
        )}

        {original.attributes?.body && (
          <Typography
            variant="body2"
            color="text.secondary"
            align="left"
            sx={{
              width: '100%',
              display: '-webkit-box',
              WebkitLineClamp: 4,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}>
            <Typography
              component="span"
              fontWeight="bold"
              color="text.primary"
              mr={0.5}>
              Body:
            </Typography>
            {original.attributes.body}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          px: 1.5,
          position: 'relative',
          top: '10px',
        }}>
        {exits.map(exit => {
          const { stroke } = getRouteStyle(nodeType, exit.label);
          return (
            <Box
              key={exit.id}
              title={exit.label}
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 1.5,
                py: 0.75,
                borderRadius: 0.5,
                minWidth: 40,
                flex: 1,
                backgroundColor: stroke || '#1976d2',
                color: 'white',
                boxShadow: 1,
                transition: 'filter 0.2s',
                '&:hover': {
                  filter: 'brightness(1.1)',
                },
              }}>
              <Typography
                variant="caption"
                fontWeight="bold"
                sx={{
                  fontSize: 10,
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                  textAlign: 'center',
                  lineHeight: 1,
                }}>
                {exit.label}
              </Typography>
              <Handle
                type="source"
                position={Position.Bottom}
                id={exit.id}
                isConnectable={!readOnly}
                style={{
                  backgroundColor: stroke,
                  borderColor: stroke,
                  width: 10,
                  height: 10,
                  border: '2px solid white',
                  bottom: -6,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 50,
                }}
              />
            </Box>
          );
        })}

        {exits.length === 0 && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              py: 0.5,
            }}>
            <Typography
              variant="caption"
              color="text.disabled"
              fontStyle="italic"
              sx={{ fontSize: 10 }}>
              Terminal Node
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
}

export default memo(CustomNode);
